// Manager-facing "Testing mode" — a demo switch in the profile dropdown.
//
// Flag helpers only. The fetch interceptor lives in testModeFetch.ts because
// it pulls in testFixtures, and that module must not be evaluated until
// demoSource has been populated (see main.tsx).
// When ON, the GET endpoints registered in testFixtures.ts are served
// from static seed data instead of the API, and AppShell shows a
// persistent banner. The flag lives in localStorage; toggling reloads
// the app so every page refetches through the interceptor.
//
// Auth, mutations, and any endpoint without a fixture pass through to
// the real API untouched — test mode changes what you SEE, it never
// writes anything.

const KEY = 'proptr_test_mode';

// Enterprise whose imported dataset is currently in play. Set by
// loadDemoSource() at boot, and ONLY when the API actually returned an
// agency set — so the id is present exactly when appending it to a
// preview URL would change what renders.
//
// A module-level value rather than storage: it is derived from the
// session, and a stale copy surviving a logout would point previews at
// the previous account's brokerage.
let demoEnterpriseId: string | null = null;

export function setDemoEnterpriseId(id: string | null): void {
  demoEnterpriseId = id;
}

export function getDemoEnterpriseId(): string | null {
  return demoEnterpriseId;
}

export function isTestMode(): boolean {
  try {
    return window.localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

export function setTestMode(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(KEY, '1');
    else window.localStorage.removeItem(KEY);
  } catch {
    // localStorage unavailable — the switch just won't persist.
  }
}

// Appends `demo=1` to a public proptree URL while test mode is on, so
// the landing app serves a demo dataset instead of querying the API for
// a proptree that was never saved. `e=<enterpriseId>` rides along when
// this brokerage has its own imported set, which is what makes the
// preview show THEIR listings and team rather than the built-in fixture
// — the landing app renders server-side and has no session to read it
// from. Without the id it falls back to the fixture, silently.
//
// No-op when test mode is off, so real pages open at their real URL.
export function withDemoFlag(url: string): string {
  if (!isTestMode()) return url;
  return `${url}${url.includes('?') ? '&' : '?'}${demoQuery()}`;
}

/** The query fragment `withDemoFlag` appends, for callers assembling
 *  their own URL (the preview iframe builds one with `preview=1`). */
export function demoQuery(): string {
  return demoEnterpriseId
    ? `demo=1&e=${encodeURIComponent(demoEnterpriseId)}`
    : 'demo=1';
}
